/**
 * Projects Page
 *
 * Full page integration for project management
 * Uses clientId and companyId from localStorage
 *
 * Features:
 * - List projects with ProjectList
 * - "Create Project" button opens ProjectForm
 * - Edit and delete project actions
 * - View project costs
 *
 * Standards: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Alert } from 'flowbite-react';
import { HiPlus, HiRefresh } from 'react-icons/hi';

import ProjectList from '../components/ProjectList';
import ProjectForm from '../components/ProjectForm';
import ProjectSelector from '../components/ProjectSelector';
import type { Project } from '../api/projects';

export const ProjectsPage: React.FC = () => {
  const navigate = useNavigate();
  const [clientId, setClientId] = useState<string>('');
  const [companyId, setCompanyId] = useState<string>('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Load client and company context from localStorage
  useEffect(() => {
    const savedClientId = localStorage.getItem('selectedClientId');
    const savedCompanyId = localStorage.getItem('selectedCompanyId');

    if (savedClientId) setClientId(savedClientId);
    if (savedCompanyId) setCompanyId(savedCompanyId);
  }, []);

  const handleCreateProject = useCallback(() => {
    setEditingProject(null);
    setIsFormOpen(true);
  }, []);

  const handleEditProject = useCallback((project: Project) => {
    setEditingProject(project);
    setIsFormOpen(true);
  }, []);

  const handleConfigureProject = useCallback((project: Project) => {
    navigate(`/projects/${project.Project_ID}/configure`);
  }, [navigate]);

  const handleCloseForm = useCallback(() => {
    setIsFormOpen(false);
    setEditingProject(null);
  }, []);

  const handleFormSuccess = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
    handleCloseForm();
  }, [handleCloseForm]);

  const handleRefresh = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
  }, []);

  const handleViewCosts = useCallback((projectId: string) => {
    console.log('[ProjectsPage] Navigating to costs page for project:', projectId);
    // Navigate to costs page with project filter
    navigate(`/costs?project=${projectId}`);
  }, [navigate]);

  if (!clientId || !companyId) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <Alert color="warning">
            <div>
              <p className="font-semibold">No Client or Company Selected</p>
              <p className="text-sm mt-1">
                Please select a client and company from the dashboard to manage projects.
              </p>
            </div>
          </Alert>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Projects</h1>
          <p className="text-gray-600 mt-1">
            Manage projects for cost attribution and budget tracking
          </p>
        </div>

        <div className="flex items-center space-x-3">
          <Button color="light" onClick={handleRefresh}>
            <HiRefresh className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button color="blue" onClick={handleCreateProject}>
            <HiPlus className="w-4 h-4 mr-2" />
            Create Project
          </Button>
        </div>
      </div>

      {/* Client/Company Context */}
      <Card>
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">
            <p>
              <span className="font-medium">Client ID:</span> {clientId}
            </p>
            <p>
              <span className="font-medium">Company ID:</span> {companyId}
            </p>
          </div>

          {/* Project Selector */}
          <div className="w-96">
            <ProjectSelector />
          </div>
        </div>
      </Card>

      {/* Project List */}
      <ProjectList
        key={refreshTrigger}
        clientId={clientId}
        companyId={companyId}
        onEditProject={handleEditProject}
        onConfigureProject={handleConfigureProject}
        onViewCosts={handleViewCosts}
        onRefresh={handleRefresh}
        showCosts={true}
      />

      {/* Project Form Modal */}
      <ProjectForm
        clientId={clientId}
        companyId={companyId}
        project={editingProject}
        isOpen={isFormOpen}
        onClose={handleCloseForm}
        onSuccess={handleFormSuccess}
      />
    </div>
  );
};

export default ProjectsPage;
